// Client-side mock friend service using localStorage
// Provides: searchUsers(query), sendRequest(senderId, receiverId), getPending(receiverId),
// respond(requestId, action), getFriends(userId), onChange(callback)

const STORAGE_KEYS = {
  USERS: 'mockUsers',
  REQUESTS: 'mockFriendRequests',
  FRIENDS: 'mockFriends'
};

const read = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const write = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};

let listeners = new Set();
const notify = () => listeners.forEach((fn) => {
  try { fn(); } catch {}
});

function uid(prefix = 'id') {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
}

function seedUsers(currentUser) {
  const existing = read(STORAGE_KEYS.USERS, null);
  if (existing && Array.isArray(existing) && existing.length > 0) return existing;
  const me = currentUser ? {
    id: String(currentUser.id || currentUser._id || 'me'),
    name: currentUser.name || currentUser.fullName || currentUser.username || 'You',
    avatar: currentUser.avatar || currentUser.avatarUrl || null,
    major: currentUser.major || 'General Studies',
    school: currentUser.school || 'Mee Academy',
    bio: currentUser.bio || 'Learning every day!'
  } : null;
  const sample = [
    { id: 'u_alex', name: 'Alex Nguyen', major: 'Computer Science', school: 'UIT', bio: 'Frontend enthusiast', avatar: null },
    { id: 'u_john', name: 'John Tran', major: 'Mathematics', school: 'HCMUS', bio: 'Numbers and proofs', avatar: null },
    { id: 'u_linh', name: 'Linh Pham', major: 'English Literature', school: 'Hanoi University', bio: 'Words matter', avatar: null },
    { id: 'u_anh', name: 'Anh Le', major: 'Economics', school: 'FTU', bio: 'Markets and data', avatar: null },
    { id: 'u_minh', name: 'Minh Vo', major: 'Physics', school: 'HCMUS', bio: 'Curious about the universe', avatar: null }
  ];
  const users = me ? [me, ...sample] : sample;
  write(STORAGE_KEYS.USERS, users);
  write(STORAGE_KEYS.REQUESTS, []);
  write(STORAGE_KEYS.FRIENDS, []);
  return users;
}

export const FriendService = {
  seed(currentUser) {
    return seedUsers(currentUser);
  },
  onChange(cb) {
    listeners.add(cb);
    return () => listeners.delete(cb);
  },
  getUsers() {
    return read(STORAGE_KEYS.USERS, []);
  },
  searchUsers(query, currentUserId) {
    const q = (query || '').trim().toLowerCase();
    const users = FriendService.getUsers();
    return users.filter(u => u.id !== currentUserId && (
      u.name?.toLowerCase().includes(q) || u.school?.toLowerCase().includes(q) || u.major?.toLowerCase().includes(q)
    ));
  },
  getFriends(userId) {
    const pairs = read(STORAGE_KEYS.FRIENDS, []);
    const friendIds = new Set();
    pairs.forEach(p => {
      if (p.a === userId) friendIds.add(p.b);
      if (p.b === userId) friendIds.add(p.a);
    });
    const users = FriendService.getUsers();
    return users.filter(u => friendIds.has(u.id));
  },
  getPending(receiverId) {
    const requests = read(STORAGE_KEYS.REQUESTS, []);
    const users = FriendService.getUsers();
    return requests.filter(r => r.to === receiverId && r.status === 'pending')
      .map(r => ({ ...r, fromUser: users.find(u => u.id === r.from) }));
  },
  getOutgoing(senderId) {
    const requests = read(STORAGE_KEYS.REQUESTS, []);
    const users = FriendService.getUsers();
    return requests.filter(r => r.from === senderId)
      .map(r => ({ ...r, toUser: users.find(u => u.id === r.to) }));
  },
  hasPendingOrFriend(a, b) {
    if (!a || !b) return false;
    const requests = read(STORAGE_KEYS.REQUESTS, []);
    const pairs = read(STORAGE_KEYS.FRIENDS, []);
    if (pairs.some(p => (p.a === a && p.b === b) || (p.a === b && p.b === a))) return true;
    if (requests.some(r => r.status === 'pending' && ((r.from === a && r.to === b) || (r.from === b && r.to === a)))) return true;
    return false;
  },
  sendRequest(from, to) {
    if (!from || !to || from === to) return { ok: false, error: 'invalid' };
    if (FriendService.hasPendingOrFriend(from, to)) return { ok: false, error: 'exists' };
    const requests = read(STORAGE_KEYS.REQUESTS, []);
    const newReq = { id: uid('req'), from, to, status: 'pending', createdAt: Date.now() };
    write(STORAGE_KEYS.REQUESTS, [newReq, ...requests]);
    notify();
    return { ok: true, request: newReq };
  },
  respond(requestId, action) {
    const requests = read(STORAGE_KEYS.REQUESTS, []);
    const idx = requests.findIndex(r => r.id === requestId);
    if (idx === -1) return { ok: false, error: 'not_found' };
    const req = requests[idx];
    if (req.status !== 'pending') return { ok: false, error: 'already' };
    if (action === 'approve') {
      req.status = 'approved';
      const friends = read(STORAGE_KEYS.FRIENDS, []);
      friends.push({ a: req.from, b: req.to, id: uid('fr') });
      write(STORAGE_KEYS.FRIENDS, friends);
    } else if (action === 'reject') {
      req.status = 'rejected';
    } else {
      return { ok: false, error: 'bad_action' };
    }
    requests[idx] = req;
    write(STORAGE_KEYS.REQUESTS, requests);
    notify();
    return { ok: true, request: req };
  }
};
